import {
  selectedRepSelector,
} from "@app-redux/selectors";
import { useAppSelector } from "@app-redux/store";
import { useRepTop30, useCategories } from "@/hooks/queries";
import { useIsMobile } from "@/hooks/useResponsive";
import { RelationshipLevelSelect } from "@components/RelationshipLevelSelect";
import { CountryFlag } from "@components/CountryFlag";
import { RepTop30Skeleton } from "@components/Skeletons";
import { ROUTER_PATHS } from "@constants/index";
import Avatar from "@mui/material/Avatar";
import { getCategoryItem, getSortedCategories, stringAvatar } from "@utils/index";
import { Link } from "react-router-dom";
import styles from "./styles.module.css";

const getSubtitleText = (repName: string, count: number): string => {
  if (count >= 30) return "";
  if (count === 0)
    return `${repName} does not have any Relationship Levels assigned yet`;
  if (count === 1)
    return `${repName} has 1 contact with a Relationship Level assigned`;
  return `${repName} has ${count} contacts with a Relationship Level assigned`;
};

export const RepTop30 = () => {
  const isMobile = useIsMobile();
  const selectedRep = useAppSelector(selectedRepSelector);
  const { data: items = [], isLoading, error } = useRepTop30(selectedRep?.id ?? null);
  const { data: categoryList, map, isCategoryMap } = useCategories();

  const getCategoryNames = (arr: string[]) =>
    arr
      .map((id) => getCategoryItem(id, categoryList, map)?.name)
      .filter(Boolean)
      .join(", ");

  const getCountryNames = (arr: string[]) =>
    arr.map((id) => {
      const item = getCategoryItem(id, categoryList, map);
      return item ? item.name : "";
    });

  if (!selectedRep) {
    return (
      <div className={styles.container}>
        <div className={styles.topPanel}>
          <h1>Rep Top 30</h1>
        </div>
        <p className={styles.emptyMessage}>Select a rep to view their Top 30.</p>
      </div>
    );
  }

  const subtitle = !isLoading ? getSubtitleText(selectedRep.name, items.length) : "";
  const totalScore = items.reduce(
    (sum, item) => sum + (item.relationship_level ?? 0),
    0
  );

  const errorMessage = error instanceof Error ? error.message : error ? String(error) : "";

  return (
    <div className={styles.container}>
      <div className={styles.topPanel}>
        <h1>{selectedRep.name}'s Top 30</h1>
        {!isLoading && items.length > 0 && (
          <span className={styles.totalScore}>Total Score: {totalScore}</span>
        )}
        {subtitle && (
          <span className={styles.resultsLabel}>{subtitle}</span>
        )}
      </div>
      <div className={styles.tableWrapper}>
        {isLoading && <RepTop30Skeleton />}
        {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
        {!isLoading && items.length === 0 && !errorMessage && (
          <p className={styles.emptyMessage}>
            No Relationship Levels assigned
          </p>
        )}
        {items.length > 0 && isMobile && (
          <div className={styles.cardList}>
            {items.map((item, index) => {
              const sortedRoles = isCategoryMap
                ? getSortedCategories(item.roles_ids ?? [], map, !!isCategoryMap)
                : item.roles_ids ?? [];
              const sortedMarkets = isCategoryMap
                ? getSortedCategories(item.markets_ids ?? [], map, !!isCategoryMap)
                : item.markets_ids ?? [];

              return (
                <div key={item.id} className={styles.card}>
                  <span className={styles.cardRank}>{index + 1}</span>
                  <Avatar
                    alt={item.name}
                    src={item.image_url}
                    {...stringAvatar(item.name)}
                    className={styles.cardAvatar}
                  />
                  <div className={styles.cardBody}>
                    <Link to={`/${ROUTER_PATHS.profile}/${item.id}`} className={styles.cardName}>
                      {item.name}
                    </Link>
                    <div className={styles.cardMeta}>
                      {getCategoryNames(sortedRoles) && (
                        <span><span className={styles.cardMetaLabel}>Role:</span> {getCategoryNames(sortedRoles)}</span>
                      )}
                      {getCategoryNames(sortedMarkets) && (
                        <span><span className={styles.cardMetaLabel}>Market:</span> {getCategoryNames(sortedMarkets)}</span>
                      )}
                    </div>
                    <div className={styles.cardLevel}>
                      <RelationshipLevelSelect
                        profileId={item.id}
                        currentLevel={item.relationship_level}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {items.length > 0 && !isMobile && (
          <table aria-label="Rep Top 30 table" className={styles.table}>
            <colgroup>
              <col width="4%" />
              <col width="22%" />
              <col width="15%" />
              <col width="15%" />
              <col width="15%" />
              <col width="18%" />
            </colgroup>
            <thead>
              <tr>
                <th className={styles.rankHeader}>#</th>
                <th>Person</th>
                <th>Relationship Level</th>
                <th>Role</th>
                <th>Market</th>
                <th>Country</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const sortedRoles = isCategoryMap
                  ? getSortedCategories(item.roles_ids ?? [], map, !!isCategoryMap)
                  : item.roles_ids ?? [];
                const sortedMarkets = isCategoryMap
                  ? getSortedCategories(item.markets_ids ?? [], map, !!isCategoryMap)
                  : item.markets_ids ?? [];
                const sortedLocations = isCategoryMap
                  ? getSortedCategories(item.locations_ids ?? [], map, !!isCategoryMap)
                  : item.locations_ids ?? [];

                return (
                  <tr key={item.id}>
                    <td className={styles.rankCell}>{index + 1}</td>
                    <td>
                      <div className={styles.profileName}>
                        <Avatar
                          alt={item.name}
                          src={item.image_url}
                          {...stringAvatar(item.name)}
                        />
                        <Link to={`/${ROUTER_PATHS.profile}/${item.id}`}>
                          <span>{item.name}</span>
                        </Link>
                      </div>
                    </td>
                    <td>
                      <RelationshipLevelSelect
                        profileId={item.id}
                        currentLevel={item.relationship_level}
                      />
                    </td>
                    <td>{getCategoryNames(sortedRoles)}</td>
                    <td>{getCategoryNames(sortedMarkets)}</td>
                    <td>
                      {getCountryNames(sortedLocations).map((c) => (
                        <div key={c} className={styles.countryBlock}>
                          <CountryFlag country={c} />
                          <p>{c}</p>
                        </div>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
